import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Wordmark";
import { toast } from "sonner";
import { errorMessage } from "@/lib/api";
import { GoogleButton, AuroraSide } from "./Login";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", org_name: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      await signup(form);
      toast.success("Your free trial has started!");
      navigate("/app");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#050505] text-white">
      <div className="flex min-h-screen">
        <div className="relative flex flex-1 items-center justify-center p-6 sm:p-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="w-full max-w-md"
          >
            <Link to="/" className="mb-10 inline-flex items-center lg:hidden" data-testid="auth-logo" aria-label="Symplee — home">
              <Wordmark variant="dark" size="lg" />
            </Link>
            <h1 className="font-display text-4xl font-light tracking-tight sm:text-5xl">
              Start your <span className="brand-text">7-day trial</span>.
            </h1>
            <p className="mt-3 text-white/55">No credit card. No contracts. Fully reversible — switch back to your old systems anytime.</p>

            <div className="mt-8 flex flex-wrap gap-3 text-xs text-white/60">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 dark:text-emerald-400" /> Every module included</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 dark:text-emerald-400" /> No upsells, no surprises</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 dark:text-emerald-400" /> Cancel anytime</span>
            </div>

            <div className="mt-8 space-y-4">
              <GoogleButton label="Sign up with Google" disabled={submitting} />
              <div className="relative my-2 flex items-center gap-3 text-xs text-white/40">
                <div className="h-px flex-1 bg-white/10" /> or with email <div className="h-px flex-1 bg-white/10" />
              </div>
              <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-white/50">Full name</Label>
                    <Input
                      required value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="border-white/10 bg-black/40 text-white"
                      data-testid="signup-name"
                      placeholder="Jane Doe"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-white/50">Organization</Label>
                    <Input
                      required value={form.org_name}
                      onChange={(e) => setForm({ ...form, org_name: e.target.value })}
                      className="border-white/10 bg-black/40 text-white"
                      data-testid="signup-org-name"
                      placeholder="Acme"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-white/50">Work email</Label>
                  <Input
                    type="email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="border-white/10 bg-black/40 text-white"
                    data-testid="signup-email"
                    placeholder="you@company.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-white/50">Password</Label>
                  <Input
                    type="password" required minLength={8} value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="border-white/10 bg-black/40 text-white"
                    data-testid="signup-password"
                    placeholder="At least 8 characters"
                  />
                </div>
                <Button type="submit" disabled={submitting} className="h-11 w-full rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="signup-submit">
                  {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Start free trial <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </form>

              <p className="text-center text-sm text-white/55">
                Already have an account?{" "}
                <Link to="/login" className="text-white underline-offset-4 hover:underline" data-testid="signup-to-login-link">
                  Sign in
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
        <AuroraSide />
      </div>
    </div>
  );
}
